<template>
    <div class="reset-page">
        <!-- Animated background -->
        <div class="bg-grid"></div>
        <div class="bg-glow bg-glow-1"></div>
        <div class="bg-glow bg-glow-2"></div>
        
        <div class="reset-container">
            <div class="reset-card">
                <!-- Logo and branding -->
                <div class="brand-section">
                    <img src="@/assets/segofy-logo.png" alt="Segofy" class="logo" />
                    <p class="tagline">Website health monitoring made simple</p>
                </div>

                <!-- Reset Password Header -->
                <div class="reset-header">
                    <h2>Reset Your Password</h2>
                    <p>Enter your new password below.</p>
                </div>

                <form name="reset-password-form" @submit.prevent="handleSubmit">
                    <!-- Password field -->
                    <div class="input-group">
                        <label for="password">New Password</label>
                        <div class="input-wrapper">
                            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            <input 
                                type="password" 
                                id="password" 
                                v-model="input.password"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                        <!-- Password requirements -->
                        <div class="password-requirements">
                            <div class="requirement" :class="{ valid: hasValidLength }">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polyline v-if="hasValidLength" points="20 6 9 17 4 12"/>
                                    <line v-else x1="18" y1="6" x2="6" y2="18"/>
                                    <line v-if="!hasValidLength" x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                <span>9-50 characters</span>
                            </div>
                            <div class="requirement" :class="{ valid: hasLetter }">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polyline v-if="hasLetter" points="20 6 9 17 4 12"/>
                                    <line v-else x1="18" y1="6" x2="6" y2="18"/>
                                    <line v-if="!hasLetter" x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                <span>One letter</span>
                            </div>
                            <div class="requirement" :class="{ valid: hasNumber }">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polyline v-if="hasNumber" points="20 6 9 17 4 12"/>
                                    <line v-else x1="18" y1="6" x2="6" y2="18"/>
                                    <line v-if="!hasNumber" x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                <span>One number</span>
                            </div>
                            <div class="requirement" :class="{ valid: hasSymbol }">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polyline v-if="hasSymbol" points="20 6 9 17 4 12"/>
                                    <line v-else x1="18" y1="6" x2="6" y2="18"/>
                                    <line v-if="!hasSymbol" x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                <span>One symbol</span>
                            </div>
                        </div>
                    </div>

                    <!-- Confirm Password field -->
                    <div class="input-group">
                        <label for="passwordConfirm">Confirm Password</label>
                        <div class="input-wrapper">
                            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            <input 
                                type="password" 
                                id="passwordConfirm" 
                                v-model="input.passwordConfirm"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                    </div>

                    <!-- Success message -->
                    <Transition name="fade">
                        <div v-if="successMessage" class="message success-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                            <span>{{ successMessage }}</span>
                        </div>
                    </Transition>

                    <!-- Error message -->
                    <Transition name="fade">
                        <div v-if="error" class="message error-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <span>{{ error }}</span>
                        </div>
                    </Transition>

                    <!-- Submit button -->
                    <button class="btn-primary" type="submit" :disabled="loading">
                        <span v-if="loading" class="spinner"></span>
                        <span v-else>Reset Password</span>
                    </button>
                </form>

                <!-- Back to login link -->
                <div class="login-link">
                    <router-link to="/login">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
                            <polyline points="15 18 9 12 15 6"/>
                        </svg>
                        Back to login
                    </router-link>
                </div>
            </div>

            <!-- Footer -->
            <p class="footer-text">
                Secure login protected by industry-standard encryption
            </p>
        </div>
    </div>
</template>

<script>
import { api } from '../utils/api'

export default {
    name: 'ResetPasswordView',
    data() {
        return {
            input: {
                password: "",
                passwordConfirm: ""
            },
            loading: false,
            error: null,
            successMessage: null,
            email: null,
            token: null
        }
    },
    computed: {
        hasLetter() {
            return /[a-zA-Z]/.test(this.input.password)
        },
        hasNumber() {
            return /\d/.test(this.input.password)
        },
        hasSymbol() {
            return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(this.input.password)
        },
        hasValidLength() {
            return this.input.password.length >= 9 && this.input.password.length <= 50
        }
    },
    created() {
        // Get email and token from URL query params
        this.email = this.$route.query.email || null
        this.token = this.$route.query.token || null

        // Validate that we have both email and token
        if (!this.email || !this.token) {
            this.error = 'Invalid password reset link. Please request a new one.'
        }
    },
    methods: {
        validatePassword(password) {
            if (password.length < 9) {
                return { valid: false, message: 'Password must be at least 9 characters long.' }
            }
            if (password.length > 50) {
                return { valid: false, message: 'Password must be no more than 50 characters long.' }
            }
            if (!/[a-zA-Z]/.test(password)) {
                return { valid: false, message: 'Password must contain at least one letter.' }
            }
            if (!/\d/.test(password)) {
                return { valid: false, message: 'Password must contain at least one number.' }
            }
            if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
                return { valid: false, message: 'Password must contain at least one symbol (!@#$%^&*()_+-=[]{}|;:,.<>?).' }
            }
            return { valid: true }
        },

        async handleSubmit() {
            // Validate password requirements
            const passwordValidation = this.validatePassword(this.input.password)
            if (!passwordValidation.valid) {
                this.error = passwordValidation.message
                return
            }

            // Validate passwords match
            if (this.input.password !== this.input.passwordConfirm) {
                this.error = 'Passwords do not match.'
                return
            }

            // Validate we have email and token
            if (!this.email || !this.token) {
                this.error = 'Invalid password reset link. Please request a new one.'
                return
            }

            this.loading = true
            this.error = null
            this.successMessage = null

            try {
                await api('/user/reset-forgot-password', {
                    method: 'POST',
                    body: JSON.stringify({
                        password: this.input.password,
                        passwordConfirm: this.input.passwordConfirm,
                        email: this.email,
                        token: this.token
                    })
                }, this.$router, { skipAuthCheck: true })

                this.successMessage = 'Your password has been successfully reset. Redirecting to login...'
                this.input.password = ""
                this.input.passwordConfirm = ""

                // Redirect to login after 2 seconds
                setTimeout(() => {
                    this.$router.push('/login?password-change=success')
                }, 2000)
            } catch (error) {
                this.error = error.code || 'Something went wrong. Please try again or request a new reset link.'
            } finally {
                this.loading = false
            }
        }
    }
}
</script>

<style scoped>
.reset-page {
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    inset: 0;
    overflow: hidden;
    background: linear-gradient(135deg, #0a0a0a 0%, #151520 50%, #0d0d15 100%);
}

/* Animated grid background */
.bg-grid {
    position: absolute;
    inset: 0;
    background-image: 
        linear-gradient(rgba(60, 98, 230, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(60, 98, 230, 0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse at center, black 0%, transparent 75%);
    animation: gridPulse 8s ease-in-out infinite;
}

@keyframes gridPulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
}

/* Floating glow effects */
.bg-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.4;
    animation: float 15s ease-in-out infinite;
}

.bg-glow-1 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(60, 98, 230, 0.3) 0%, transparent 70%);
    top: -200px;
    right: -100px;
    animation-delay: 0s;
}

.bg-glow-2 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(94, 194, 105, 0.2) 0%, transparent 70%);
    bottom: -150px;
    left: -100px;
    animation-delay: -7s;
}

@keyframes float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -30px) scale(1.05); }
    66% { transform: translate(-20px, 20px) scale(0.95); }
}

.reset-container {
    position: relative;
    z-index: 10;
    width: 100%;
    max-width: 420px;
    padding: 20px;
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.reset-card {
    background: rgba(24, 24, 25, 0.8);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    padding: 40px 36px;
    box-shadow: 
        0 25px 50px -12px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.05) inset;
}

/* Brand section */
.brand-section {
    text-align: center;
    margin-bottom: 24px;
}

.logo {
    height: 36px;
    width: auto;
    margin-bottom: 12px;
}

.tagline {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin: 0;
    letter-spacing: 0.02em;
}

/* Reset header */
.reset-header {
    text-align: center;
    margin-bottom: 28px;
}

.reset-header h2 {
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 10px 0;
}

.reset-header p {
    font-size: 0.9rem;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.5;
}

/* Form elements */
.input-group {
    margin-bottom: 20px;
}

.input-group label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-secondary);
    margin-bottom: 8px;
    letter-spacing: 0.02em;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 14px;
    width: 18px;
    height: 18px;
    color: var(--text-muted);
    pointer-events: none;
    transition: color 0.2s ease;
}

.input-wrapper:focus-within .input-icon {
    color: var(--accent-color);
}

.input-group input {
    width: 100%;
    padding: 14px 14px 14px 46px;
    font-size: 0.95rem;
    font-family: inherit;
    color: var(--text-primary);
    background: rgba(61, 61, 61, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    outline: none;
    transition: all 0.2s ease;
}

.input-group input::placeholder {
    color: var(--text-muted);
}

.input-group input:hover {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(61, 61, 61, 0.4);
}

.input-group input:focus {
    border-color: var(--accent-color);
    background: rgba(61, 61, 61, 0.5);
    box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

/* Password requirements */
.password-requirements {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 12px;
    margin-top: 10px;
    padding: 12px;
    background: rgba(61, 61, 61, 0.2);
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.requirement {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: var(--text-muted);
    transition: color 0.2s ease;
}

.requirement svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

.requirement.valid {
    color: var(--success-color);
}

/* Messages */
.message {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    margin-bottom: 20px;
    font-size: 0.9rem;
    line-height: 1.5;
}

.message svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 2px;
}

.success-message {
    background: rgba(94, 194, 105, 0.1);
    border: 1px solid rgba(94, 194, 105, 0.2);
    color: var(--success-color);
}

.error-message {
    background: rgba(221, 82, 76, 0.1);
    border: 1px solid rgba(221, 82, 76, 0.2);
    color: var(--error-color);
}

/* Buttons */
.btn-primary {
    width: 100%;
    padding: 14px 24px;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    color: white;
    background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 15px rgba(60, 98, 230, 0.3);
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(60, 98, 230, 0.4);
}

.btn-primary:active:not(:disabled) {
    transform: translateY(0);
}

.btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Spinner */
.spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Login link */
.login-link {
    text-align: center;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.login-link a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.9rem;
    color: var(--accent-color);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
}

.login-link a:hover {
    color: #5a7ff0;
}

.login-link a svg {
    transition: transform 0.2s ease;
}

.login-link a:hover svg {
    transform: translateX(-3px);
}

/* Footer text */
.footer-text {
    text-align: center;
    margin-top: 24px;
    font-size: 0.75rem;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 768px) {
    .reset-container {
        padding: 12px;
    }

    .reset-card {
        padding: 28px 24px;
        border-radius: 16px;
    }

    .brand-section {
        margin-bottom: 20px;
    }

    .logo {
        height: 32px;
    }

    .tagline {
        font-size: 0.85rem;
    }

    .reset-header {
        margin-bottom: 24px;
    }

    .reset-header h2 {
        font-size: 1.25rem;
    }

    .input-group {
        margin-bottom: 16px;
    }

    .input-group input {
        padding: 12px 12px 12px 42px;
    }

    .password-requirements {
        grid-template-columns: 1fr;
        gap: 4px;
    }

    .btn-primary {
        padding: 12px 20px;
    }

    .login-link {
        margin-top: 20px;
        padding-top: 18px;
    }

    .footer-text {
        margin-top: 16px;
        font-size: 0.7rem;
    }

    .bg-glow-1 {
        width: 300px;
        height: 300px;
    }

    .bg-glow-2 {
        width: 250px;
        height: 250px;
    }
}

@media (max-width: 480px) {
    .reset-container {
        padding: 8px;
    }

    .reset-card {
        padding: 24px 20px;
    }

    .brand-section {
        margin-bottom: 16px;
    }

    .logo {
        height: 28px;
        margin-bottom: 8px;
    }

    .tagline {
        font-size: 0.8rem;
    }

    .reset-header {
        margin-bottom: 20px;
    }

    .reset-header h2 {
        font-size: 1.15rem;
    }

    .input-group label {
        font-size: 0.8rem;
        margin-bottom: 6px;
    }

    .message {
        padding: 12px 14px;
        font-size: 0.85rem;
    }

    .login-link {
        margin-top: 18px;
        padding-top: 16px;
        font-size: 0.85rem;
    }

    .footer-text {
        margin-top: 12px;
    }
}
</style>

